import Model, { attr, belongsTo, type AsyncBelongsTo } from '@ember-data/model';
import { service } from '@ember/service';
import type { Type } from '@warp-drive/core-types/symbols';
import type IntlService from 'ember-intl/services/intl';
import type OrganisationService from 'scaleflow/services/organisation';
import { DateTime } from 'scaleflow/utils/date-time';
import localize from 'scaleflow/utils/localize';
import placeDecimal from 'scaleflow/utils/place-decimal';
import type MetricModel from './metric';

export default class MeasurementModel extends Model {
  declare [Type]: 'measurement';
  @service declare intl: IntlService;
  @service declare organisation: OrganisationService;

  @belongsTo('metric', {
    async: true,
    inverse: 'measurements',
  })
  declare metric: AsyncBelongsTo<MetricModel>;

  @attr('number') declare value: number;
  @attr('number', { defaultValue: 2 }) declare decimals: number;
  @attr('', {
    defaultValue: () => {
      return {
        ops: [],
      };
    },
  })
  declare comment: unknown; //type text_delta
  @attr declare measured_at_utc: string;
  @attr('boolean', { defaultValue: false }) declare increment: boolean;

  get formattedValue() {
    const metric = this.metric.content;
    const symbol = metric?.symbol;
    const abs = Math.abs(this.value);
    const preciseValue = placeDecimal(abs, this.decimals);
    let args: Parameters<typeof localize>;
    if (symbol === 'percentage')
      args = [
        preciseValue,
        '%',
        undefined,
        this.organisation.locale,
        this.decimals,
        this.decimals,
      ];
    else if (symbol === 'currency')
      args = [
        preciseValue,
        ',-',
        this.intl.t('currency.euro'),
        this.organisation.locale,
        this.decimals,
        this.decimals,
      ];
    else
      args = [
        preciseValue,
        '',
        undefined,
        this.organisation.locale,
        this.decimals,
        this.decimals,
      ];

    const formatted = localize(...args);

    if (this.increment) {
      const sign = this.value >= 0 ? '+ ' : '- ';
      return `${sign}${formatted}`;
    }

    return formatted;
  }

  get formattedRecordedDate() {
    return DateTime.from(this.measured_at_utc).format('DD MMM, YYYY');
  }
}
